<div class="qt-chat" (click)="onChatAreaClick($event)">
  <div class="qt-chat__header sr-only" aria-live="polite">
    {{ 'GNR_AI_CHAT' | translate }}
  </div>

  <div
    class="qt-chat__scroll chat-body-inner"
    [class.qt-chat__scroll--empty]="isEmptyChat()"
    #scroll>
    @if (isEmptyChat()) {
      <div class="qt-chat__welcome">
        <span class="qt-chat__welcome-avatar" aria-hidden="true">
          <mat-icon class="qt-chat__welcome-mark">auto_awesome</mat-icon>
        </span>
        <h4 class="qt-chat__welcome-title">Ask Allo about your deals</h4>
        <p class="qt-chat__welcome-subtitle">
          {{ 'GNR_AI_CHAT_HELP' | translate }}
        </p>
        <div class="qt-chat__chips">
          @for (chip of starterPrompts; track chip) {
            <button
              type="button"
              class="qt-chat__chip"
              [disabled]="hasPendingApproval()"
              (click)="sendPrompt(chip)">
              {{ chip }}
            </button>
          }
        </div>
      </div>
    }

    @for (m of allMessages(); track m.id; let i = $index) {
      <qt-ai-chat-enhanced-message-bubble
        [message]="m"
        [previousMessageText]="getPreviousMessageText(i)"
        [toolStatusLabel]="m.streaming ? toolStatusLabel() : null"
        (retry)="retryFrom(i)">
      </qt-ai-chat-enhanced-message-bubble>
    }

    @if (showTyping()) {
      <qt-ai-chat-enhanced-message-bubble
        [isTyping]="true"
        [typingDots]="typingDots"
        [message]="typingMessage"
        [toolStatusLabel]="toolStatusLabel()">
      </qt-ai-chat-enhanced-message-bubble>
    }

    @if (stillWorking()) {
      <div class="qt-chat__still-working" role="status" aria-live="polite">
        {{ 'GNR_ALLO_STILL_WORKING' | translate }}
      </div>
    }

    @for (pa of pendingApprovals(); track pa.pendingApprovalId) {
      <qt-ai-chat-approval-card
        [payload]="pa.payload"
        [pendingApprovalId]="pa.pendingApprovalId"
        [chatId]="currentChatId() ?? ''"
        (resolved)="onApprovalResolved(pa.pendingApprovalId)">
      </qt-ai-chat-approval-card>
    }
  </div>

  <div
    #inputArea
    class="qt-chat__input chat-input-area"
    [class.qt-chat__input--dragging]="isDropActive()"
    (dragenter)="onDragEnter($event)"
    (dragover)="onDragOver($event)"
    (dragleave)="onDragLeave($event)"
    (drop)="onDrop($event)">
    @if (isDropActive()) {
      <div class="qt-chat__dropzone" aria-hidden="true">
        <mat-icon>cloud_upload</mat-icon>
        <span>{{ 'GNR_DROP_FILES_UPLOAD' | translate }}</span>
      </div>
    }

    @if (attachmentFormatError(); as errorMessage) {
      <div class="qt-chat__format-error" role="alert">
        {{ errorMessage }}
      </div>
    }

    @if (hasPendingApproval()) {
      <div class="qt-chat__pending-hint" role="status">
        {{ 'GNR_ALLO_RESOLVE_PENDING' | translate }}
      </div>
    }

    @if (attachedFiles().length || pendingFilesForDisplay().length) {
      <div class="qt-chat__attachments" aria-label="Attached files">
        @for (f of attachedFiles(); track f.id) {
          <span class="qt-chat__attachment-chip">
            <span class="qt-chat__attachment-name">{{ f.name }}</span>
            <span class="qt-chat__attachment-type">{{
              fileTypeLabel(f.type)
            }}</span>
            <button
              type="button"
              class="qt-chat__attachment-remove"
              (click)="removeAttachedFile(f.id)"
              [attr.aria-label]="'GNR_REMOVE_ATTACHMENT' | translate">
              <mat-icon>close</mat-icon>
            </button>
          </span>
        }
        @for (f of pendingFilesForDisplay(); track f.key) {
          <span
            class="qt-chat__attachment-chip qt-chat__attachment-chip--pending">
            <span class="qt-chat__attachment-name">{{ f.name }}</span>
            <span class="qt-chat__attachment-type">{{
              fileTypeLabel(f.type)
            }}</span>
            <button
              type="button"
              class="qt-chat__attachment-remove"
              (click)="removePendingFile(f.key)"
              [attr.aria-label]="'GNR_REMOVE_ATTACHMENT' | translate">
              <mat-icon>close</mat-icon>
            </button>
          </span>
        }
      </div>
    }

    <div class="qt-chat__prompt input-wrapper">
      <input
        #filePicker
        id="qt-chat-file-picker"
        type="file"
        hidden
        multiple
        (change)="onFilesSelected($event)"
        [attr.accept]="allowedAttachmentAccept" />
      <button
        class="qt-attach attach-btn"
        type="button"
        (click)="openFilePicker()"
        [disabled]="!canAttach()"
        aria-label="Attach files">
        <mat-icon>attach_file</mat-icon>
      </button>
      <textarea
        #chatInput
        class="qt-input qt-input-field chat-input"
        rows="1"
        maxlength="4000"
        placeholder="{{ 'GNR_AI_CHAT_ASK' | translate }}..."
        aria-label="Ask anything"
        [disabled]="hasPendingApproval()"
        [ngModel]="draft()"
        (ngModelChange)="draft.set($event)"
        (input)="growChatInput()"
        (keydown.enter)="onChatKeydown($event)"></textarea>

      <!--
        SD-2765: while Allo is responding the send arrow becomes a stop control, in the same slot
        and the same size, per the mocks. Side effect: a second message can no longer be queued
        mid-turn, which today's UI allows. That trade-off is still open with product (SD-2487) —
        the mocks show this swap and it matches ChatGPT/Claude, so it is the default until told
        otherwise; giving stop its own control is the alternative.
      -->
      @if (isResponding()) {
        <span class="qt-send-wrap">
          <!--
            SD-2487: the stop control is slate with a solid white square, not the send button's
            accent with an outlined glyph. Stopping is a neutral action, and the mocks give it its
            own treatment rather than inheriting the send styling.
          -->
          <button
            class="qt-send qt-send--stop send-btn"
            type="button"
            (click)="stop()"
            aria-label="Stop">
            <!--
              An 18-unit viewBox on the 18px icon box, so a user unit is a CSS pixel and the
              square below renders at the 10px Ellie's mock specifies. On the shared 24-unit
              grid the same rect scaled to 7.5px and read as a dot.
            -->
            <svg
              class="qt-send__icon"
              viewBox="0 0 18 18"
              width="18"
              height="18"
              fill="currentColor"
              aria-hidden="true">
              <rect x="4" y="4" width="10" height="10" rx="2" />
            </svg>
          </button>
        </span>
      } @else {
        <span
          class="qt-send-wrap"
          matTooltip="Type a message to send"
          [matTooltipDisabled]="canSend()"
          matTooltipPosition="above">
          <button
            class="qt-send send-btn"
            type="button"
            (click)="send()"
            [disabled]="!canSend()"
            aria-label="Send">
            <svg
              class="qt-send__icon"
              viewBox="0 -960 960 960"
              width="24"
              height="24"
              fill="currentColor"
              aria-hidden="true">
              <path
                d="M440-160v-487L216-423l-56-57 320-320 320 320-56 57-224-224v487h-80Z" />
            </svg>
          </button>
        </span>
      }
    </div>
  </div>
</div>
